import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LIMITED_AUTOMATIONS, readyForAuto, type AutomationLogEntry, type AutonomyChange, type AutonomyLevel, type AutonomySetting } from '@syndiqia/platform/automation';
import { useState } from 'react';
import { errorMessage, useApi } from '../api';
import { Alert, Empty, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';

interface AutomationData {
  settings: AutonomySetting[];
  history: AutonomyChange[];
  log: AutomationLogEntry[];
}

/** Autonomy of each automated process: confirmation by a person (default) or automatic, and what ran on its own. */
export function AutomationPage() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const queryClient = useQueryClient();
  const a = t.automation;
  const data = useQuery({ queryKey: [s.tenantId, 'automation'], queryFn: () => api<AutomationData>(`${s.api}/automation`), enabled: s.can('automation.view') });
  const change = useMutation({
    mutationFn: ({ process, level, note, limitCents }: { process: string; level: AutonomyLevel; note: string; limitCents?: number | null }) =>
      api(`${s.api}/automation/${process}`, { method: 'PUT', body: { level, note, ...(limitCents !== undefined ? { limitCents } : {}) } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [s.tenantId, 'automation'] }),
  });
  if (!s.can('automation.view')) return <Alert tone="info">{a.boardOnly}</Alert>;
  const editable = s.can('automation.configure');
  const since = Date.now() - 30 * 86_400_000;
  const day = (iso: string) => date(iso.slice(0, 10));
  const set = (process: AutonomySetting['process'], level: AutonomyLevel, limitCents?: number | null) => {
    const note = window.prompt(a.confirm(a.processes[process].title, a.levels[level]), '');
    if (note !== null) change.mutate({ process, level, note, limitCents });
  };

  return (
    <>
      <PageHeader title={a.title} intro={a.intro} />
      {data.isPending && <Loading label={t.loading} />}
      {data.isError && <Alert>{errorMessage(data.error, t)}</Alert>}
      {change.isError && <Alert>{errorMessage(change.error, t)}</Alert>}
      {data.data && (
        <>
          <div className="automation-list">
            {data.data.settings.map((setting) => {
              const p = a.processes[setting.process];
              const recent = data.data.log.filter((l) => l.process === setting.process && Date.parse(l.createdAt) >= since).length;
              return (
                <section key={setting.process} className={`panel automation automation-${setting.level}`}>
                  <header className="dash-card-header">
                    <h2>{p.title}</h2>
                    <div className="segmented" role="group" aria-label={p.title}>
                      {(['confirm', 'auto'] as const).map((level) => (
                        <button
                          key={level}
                          type="button"
                          className={setting.level === level ? 'active' : undefined}
                          disabled={!editable || change.isPending || setting.level === level || (level === 'auto' && LIMITED_AUTOMATIONS.includes(setting.process) && !setting.limitCents)}
                          onClick={() => set(setting.process, level)}
                        >
                          {a.levels[level]}
                        </button>
                      ))}
                    </div>
                  </header>
                  <p className="small">
                    <strong>{a.levels.confirm} :</strong> {p.confirm}
                  </p>
                  <p className="small">
                    <strong>{a.levels.auto} :</strong> {p.auto}
                  </p>
                  <p className="muted small">
                    {a.guardrails} : {p.guardrails}
                  </p>
                  {LIMITED_AUTOMATIONS.includes(setting.process) && (
                    <LimitField setting={setting} editable={editable} busy={change.isPending} onSave={(limitCents) => set(setting.process, setting.level, limitCents)} />
                  )}
                  <p className="small">
                    {setting.accuracy.total === 0 ? (
                      <span className="muted">{a.noDecisions}</span>
                    ) : (
                      <>
                        {a.accuracy(setting.accuracy.agreed, setting.accuracy.total)}
                        {setting.level === 'confirm' && readyForAuto(setting.accuracy) && <span className="badge badge-ready"> {a.ready}</span>}
                      </>
                    )}
                  </p>
                  <p className="muted small">
                    {setting.updatedAt ? a.changed(day(setting.updatedAt), setting.updatedBy ?? '—') : a.default}
                    {setting.level === 'auto' && ` · ${a.recent(recent)}`}
                  </p>
                </section>
              );
            })}
          </div>
          {!editable && <p className="muted small">{a.directorsOnly}</p>}

          <section className="panel">
            <h2>{a.alwaysHumanTitle}</h2>
            <p className="muted small">{a.alwaysHumanIntro}</p>
            <ul>
              {a.alwaysHuman.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </section>

          <section className="panel">
            <h2>{a.logTitle}</h2>
            {data.data.log.length === 0 ? (
              <Empty>{a.logNone}</Empty>
            ) : (
              <ul className="compact-list">
                {data.data.log.map((l) => (
                  <li key={l.id}>
                    <span>
                      <span className="badge">{a.processes[l.process as AutonomySetting['process']]?.title ?? l.process}</span> {l.summary}
                    </span>
                    <span className="muted small">{day(l.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <IntegrityPanel />

          {data.data.history.length > 0 && (
            <section className="panel">
              <h2>{a.historyTitle}</h2>
              <ul className="compact-list">
                {data.data.history.map((h) => (
                  <li key={h.id}>
                    <span>
                      {a.processes[h.process as AutonomySetting['process']]?.title ?? h.process} → <strong>{a.levels[h.level]}</strong>
                      {h.limitCents ? ` (${a.limitShort(money(h.limitCents))})` : ''}
                      {h.note && <span className="muted"> · {h.note}</span>}
                    </span>
                    <span className="muted small">
                      {day(h.changedAt)} · {h.changedBy ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </>
  );
}

/** The amount limit of a process (e.g. dispatch): directors set it; automatic mode needs one. */
function LimitField({ setting, editable, busy, onSave }: { setting: AutonomySetting; editable: boolean; busy: boolean; onSave: (limitCents: number) => void }) {
  const { t, money } = useI18n();
  const a = t.automation;
  const label = (a.processes[setting.process] as { limitLabel?: string }).limitLabel ?? a.limitLabel;
  const [value, setValue] = useState(setting.limitCents ? String(setting.limitCents / 100) : '');
  const cents = Math.round(Number(value.replace(',', '.').replace(/[\s$]/g, '')) * 100);
  const valid = value.trim() !== '' && Number.isFinite(cents) && cents > 0;
  return (
    <div className="automation-limit">
      <span className="small">
        {label} : <strong>{setting.limitCents ? money(setting.limitCents) : a.limitNone}</strong>
      </span>
      {editable && (
        <span className="automation-limit-edit">
          <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} aria-label={label} placeholder="500" />
          <button type="button" className="button button-small" disabled={busy || !valid || cents === setting.limitCents} onClick={() => onSave(cents)}>
            {a.limitSave}
          </button>
        </span>
      )}
    </div>
  );
}

interface AuditIntegrity {
  streams: { stream: 'outbox' | 'automation_log'; checked: number; firstBroken: number | null; headSeq: number }[];
  anchor: { date: string; matches: boolean } | null;
  intact: boolean;
  checkedAt: string;
}

/** Recomputes the chained audit trail (events and automatic actions) and compares it with the last external anchor. */
function IntegrityPanel() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const api = useApi();
  const a = t.automation;
  const check = useMutation({ mutationFn: () => api<AuditIntegrity>(`${s.api}/automation/integrity`) });
  const r = check.data;
  const count = (name: 'outbox' | 'automation_log') => r?.streams.find((x) => x.stream === name);
  return (
    <section className="panel">
      <h2>{a.integrityTitle}</h2>
      <p className="muted small">{a.integrityIntro}</p>
      {r && (
        <Alert tone={r.intact ? 'info' : undefined}>
          {r.intact ? a.integrityOk(count('outbox')?.checked ?? 0, count('automation_log')?.checked ?? 0) : a.integrityBroken}
          {r.streams
            .filter((x) => x.firstBroken !== null)
            .map((x) => (
              <span key={x.stream}> {a.integrityBrokenAt(a.integrityStreams[x.stream], x.firstBroken!)}</span>
            ))}{' '}
          {r.anchor ? (r.anchor.matches ? a.integrityAnchorOk(date(r.anchor.date)) : a.integrityAnchorMismatch(date(r.anchor.date))) : a.integrityNoAnchor}
        </Alert>
      )}
      {check.isError && <Alert>{errorMessage(check.error, t)}</Alert>}
      <div className="form-actions">
        <button type="button" className="button button-small" disabled={check.isPending} onClick={() => check.mutate()}>
          {a.integrityCheck}
        </button>
      </div>
    </section>
  );
}
